import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Tag from "@/components/base/Tag";
import NewsCard from "@/components/feature/NewsCard";
import { news } from "@/mocks/news";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function NewsDetail() {
  const { id } = useParams();
  const item = news.find((n) => n.id === id);
  const [toast, setToast] = useState("");

  useDocumentMeta(
    item
      ? { title: `${item.title} - 美国禁忌4 资讯`, description: item.summary }
      : { title: "资讯详情 - 美国禁忌4", description: "美国禁忌4资讯详情。" }
  );

  if (!item) {
    return (
      <div className="px-4 md:px-10 py-16 text-center">
        <h1 className="font-heading text-2xl text-foreground-950">未找到该资讯</h1>
        <p className="mt-3 text-sm text-foreground-500">你访问的资讯可能已被移除或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-label text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = news
    .filter((n) => n.id !== item.id)
    .map((n) => ({
      n,
      score: n.tags.filter((t) => item.tags.includes(t)).length + (n.category === item.category ? 1 : 0),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((r) => r.n);

  const shareTitle = item.title;

  const copyLink = async (channel: string) => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        setToast(channel === "wechat" ? "链接已复制，请打开微信粘贴分享" : "链接已复制到剪贴板");
      } else {
        setToast("请手动复制浏览器地址栏链接进行分享");
      }
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
    window.setTimeout(() => setToast(""), 2600);
  };

  const shareTargets = [
    {
      key: "wechat",
      label: "微信",
      icon: "ri-wechat-line",
      onClick: () => copyLink("wechat"),
    },
    {
      key: "weibo",
      label: "微博",
      icon: "ri-weibo-line",
      href: `https://frressh.com/share/share.php?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(shareTitle)}`,
    },
    {
      key: "qq",
      label: "QQ",
      icon: "ri-qq-line",
      href: `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(shareTitle)}`,
    },
    {
      key: "copy",
      label: "复制链接",
      icon: "ri-link",
      onClick: () => copyLink("copy"),
    },
  ];

  return (
    <div className="px-4 md:px-10 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "最新资讯", to: "/news" },
          { label: item.category, to: "/news" },
          { label: item.title },
        ]}
      />

      <article className="mt-6 max-w-4xl">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Tag tone="accent">{item.category}</Tag>
          <span className="text-sm text-foreground-500 font-label">
            <i className="ri-calendar-line mr-1" />
            {item.date}
          </span>
          <span className="text-sm text-foreground-500 font-label">
            <i className="ri-user-line mr-1" />
            {item.author}
          </span>
        </div>

        <h1 className="font-heading text-2xl md:text-4xl font-semibold tracking-wide text-foreground-950 leading-snug">
          {item.title}
        </h1>
        <p className="mt-4 text-[15px] md:text-base text-foreground-500 leading-relaxed border-l-2 border-accent-500 pl-4">
          {item.summary}
        </p>

        <div className="mt-6 w-full h-[240px] md:h-[400px] rounded-lg overflow-hidden">
          <img
            src={item.cover}
            alt={`${item.title} 配图`}
            title={`${item.title} - 美国禁忌4 资讯`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5">
          {item.content.map((p, i) => (
            <p key={i} className="text-[15px] md:text-base leading-8 text-foreground-700">
              {p}
            </p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {item.tags.map((t) => (
            <Tag key={t} tone="secondary">
              #{t}
            </Tag>
          ))}
        </div>

        {/* Share */}
        <div className="mt-10 pt-6 border-t border-background-200">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-foreground-500 font-label">分享到</span>
            {shareTargets.map((s) =>
              s.href ? (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3.5 py-2 text-sm text-foreground-700 hover:border-accent-500/50 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${s.icon} text-base`} />
                  {s.label}
                </a>
              ) : (
                <button
                  key={s.key}
                  type="button"
                  onClick={s.onClick}
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3.5 py-2 text-sm text-foreground-700 hover:border-accent-500/50 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${s.icon} text-base`} />
                  {s.label}
                </button>
              )
            )}
          </div>
          {toast && (
            <p className="mt-3 inline-flex items-center gap-2 rounded-md bg-background-100 border border-background-200 px-3 py-2 text-sm text-accent-400 animate-fade-in">
              <i className="ri-check-line" />
              {toast}
            </p>
          )}
        </div>
      </article>

      <section className="mt-12 md:mt-16">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-6 h-[2px] bg-accent-500 inline-block" />
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide">相关文章推荐</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
          {related.map((n) => (
            <NewsCard key={n.id} item={n} />
          ))}
        </div>
      </section>
    </div>
  );
}